import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Check, Plus, Trash2, ChevronUp, ChevronDown, Loader2,
  Image as ImageIcon, Menu, Eye, Search, Heart, ShoppingCart, User,
  ChevronRight, Upload, X, CheckCircle, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

const NavbarForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    logo: {
      type: 'text',
      text: '',
      imageUrl: '',
      height: 40
    },
    menuItems: [
      { id: '1', label: 'Home', link: '/', type: 'link', enabled: true, children: [] },
      { id: '2', label: 'Shop', link: '/shop', type: 'link', enabled: true, children: [] },
      { id: '3', label: 'About', link: '/about', type: 'link', enabled: true, children: [] },
      { id: '4', label: 'Contact', link: '/contact', type: 'link', enabled: true, children: [] }
    ],
    showSearch: true,
    showWishlist: true,
    showCart: true,
    showAccount: true,
    stickyNav: true,
    cta: {
      enabled: false,
      text: 'Shop Now',
      link: '/shop'
    }
  });

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/template/${templateId}/page/navbar`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      // Try to load from structure.attributes first (new format), fallback to content (old format)
      const attributes = response.data.structure?.attributes || response.data.content;
      if (attributes) {
        // Map CloudFront structure fields to form fields
        const mappedData = {
          logo: {
            type: attributes.logoUrl ? 'image' : 'text',
            text: attributes.brandName || attributes.logo?.text || '',
            imageUrl: attributes.logoUrl || attributes.logo?.imageUrl || '',
            height: attributes.logo?.height || 40
          },
          // Convert navLinks to menuItems format
          menuItems: (attributes.navLinks || attributes.menuItems || []).map((item, idx) => ({
            id: item.id || String(idx + 1),
            label: item.label || '',
            link: item.path || item.link || '/',
            type: 'link',
            enabled: item.enabled !== false,
            children: item.children || []
          })),
          showSearch: attributes.showSearch !== false,
          showWishlist: attributes.showWishlist !== false,
          showCart: attributes.showCartIcon !== false && attributes.showCart !== false,
          showAccount: attributes.showUserProfile !== false && attributes.showAccount !== false,
          stickyNav: attributes.enableScrollEffect !== false && attributes.stickyNav !== false,
          cta: attributes.cta || { enabled: false, text: 'Shop Now', link: '/shop' }
        };

        setFormData(prev => ({ ...prev, ...mappedData }));

        // Handle logo preview
        if (mappedData.logo.imageUrl) {
          setLogoPreview(mappedData.logo.imageUrl);
        }
      }
    } catch (error) {
      console.error('Error loading navbar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      const formDataToSend = new FormData();
      formDataToSend.append('status', isDraft ? 'draft' : 'complete');

      // Prepare data with both old and new field mappings for compatibility
      const dataToSave = {
        ...formData,
        // Map to new NavbarWrapper attributes format
        brandName: formData.logo.text,
        logoUrl: formData.logo.imageUrl,
        logoAlt: formData.logo.text,
        // Convert menuItems to navLinks format
        navLinks: formData.menuItems.map(item => ({
          label: item.label,
          path: item.link,
          enabled: item.enabled
        })),
        showCartIcon: formData.showCart,
        showUserProfile: formData.showAccount,
        enableScrollEffect: formData.stickyNav
      };
      formDataToSend.append('content', JSON.stringify(dataToSave));

      if (logoFile) {
        formDataToSend.append('logo_image', logoFile);
      }

      await axios.post(
        `${API_BASE_URL}/api/template/${templateId}/page/navbar/save`,
        formDataToSend,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  const addMenuItem = () => {
    setFormData(prev => ({
      ...prev,
      menuItems: [...prev.menuItems, {
        id: generateId(),
        label: '',
        link: '',
        type: 'link',
        enabled: true,
        children: []
      }]
    }));
  };

  const updateMenuItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeMenuItem = (index) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.filter((_, i) => i !== index)
    }));
  };

  const moveMenuItem = (index, direction) => {
    const newItems = [...formData.menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setFormData(prev => ({ ...prev, menuItems: newItems }));
  };

  const toggleMenuItemType = (index) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.map((item, i) =>
        i === index ? {
          ...item,
          type: item.type === 'link' ? 'dropdown' : 'link',
          children: item.type === 'link' ? [{ label: '', link: '' }] : []
        } : item
      )
    }));
  };

  const addDropdownChild = (menuIndex) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.map((item, i) =>
        i === menuIndex ? {
          ...item,
          children: [...item.children, { label: '', link: '' }]
        } : item
      )
    }));
  };

  const updateDropdownChild = (menuIndex, childIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.map((item, i) =>
        i === menuIndex ? {
          ...item,
          children: item.children.map((child, j) =>
            j === childIndex ? { ...child, [field]: value } : child
          )
        } : item
      )
    }));
  };

  const removeDropdownChild = (menuIndex, childIndex) => {
    setFormData(prev => ({
      ...prev,
      menuItems: prev.menuItems.map((item, i) =>
        i === menuIndex ? {
          ...item,
          children: item.children.filter((_, j) => j !== childIndex)
        } : item
      )
    }));
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your navigation bar has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#D45614] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading navigation...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Navigation Bar</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">Saved {lastSaved.toLocaleTimeString()}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium rounded-lg hover:bg-[#c04d12] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Logo Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Logo</h2>
              <p className="text-sm text-gray-500">Configure your brand logo</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, logo: { ...prev.logo, type: 'text' } }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.logo.type === 'text'
                      ? 'border-[#D45614] bg-[#D45614]/5 text-[#D45614]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Text Logo
                </button>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, logo: { ...prev.logo, type: 'image' } }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.logo.type === 'image'
                      ? 'border-[#D45614] bg-[#D45614]/5 text-[#D45614]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Image Logo
                </button>
              </div>
            </div>

            {formData.logo.type === 'text' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.logo.text}
                  onChange={(e) => setFormData(prev => ({ ...prev, logo: { ...prev.logo, text: e.target.value } }))}
                  placeholder="Your Brand Name"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Image</label>
                  {logoPreview ? (
                    <div className="relative inline-block">
                      <img src={logoPreview} alt="Logo preview" className="h-16 object-contain border rounded-lg p-2" />
                      <button
                        onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D45614] hover:bg-[#D45614]/5 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload logo</span>
                      <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</span>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Or use URL</label>
                  <input
                    type="url"
                    value={formData.logo.imageUrl}
                    onChange={(e) => { setFormData(prev => ({ ...prev, logo: { ...prev.logo, imageUrl: e.target.value } })); setLogoPreview(e.target.value); }}
                    placeholder="https://example.com/logo.png"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Height (px)</label>
                  <input
                    type="number"
                    value={formData.logo.height}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo: { ...prev.logo, height: parseInt(e.target.value) || 40 } }))}
                    min="20"
                    max="100"
                    className="w-32 px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Recommended: 40-60px</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Menu Items Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <Menu className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Menu Items</h2>
                <p className="text-sm text-gray-500">Add links and dropdown menus</p>
              </div>
            </div>
            <button
              onClick={addMenuItem}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#D45614] bg-[#D45614]/5 hover:bg-[#D45614]/10 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          {formData.menuItems.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <Menu className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-3">No menu items yet</p>
              <button
                onClick={addMenuItem}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#D45614] hover:bg-[#c04d12] rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Item
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {formData.menuItems.map((item, index) => (
                <div key={item.id || index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveMenuItem(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveMenuItem(index, 'down')}
                        disabled={index === formData.menuItems.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => updateMenuItem(index, 'label', e.target.value)}
                        placeholder="Label"
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={item.link}
                        onChange={(e) => updateMenuItem(index, 'link', e.target.value)}
                        placeholder="/page-url"
                        disabled={item.type === 'dropdown'}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                      />
                      <select
                        value={item.type}
                        onChange={(e) => toggleMenuItemType(index)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                      >
                        <option value="link">Simple Link</option>
                        <option value="dropdown">Dropdown</option>
                      </select>
                    </div>

                    <button
                      onClick={() => removeMenuItem(index)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Dropdown Children */}
                  {item.type === 'dropdown' && (
                    <div className="mt-4 ml-10 pl-4 border-l-2 border-[#D45614]/20 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Dropdown Items</p>
                      {item.children?.map((child, childIndex) => (
                        <div key={childIndex} className="flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                          <input
                            type="text"
                            value={child.label}
                            onChange={(e) => updateDropdownChild(index, childIndex, 'label', e.target.value)}
                            placeholder="Sub-item label"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                          />
                          <input
                            type="text"
                            value={child.link}
                            onChange={(e) => updateDropdownChild(index, childIndex, 'link', e.target.value)}
                            placeholder="/sub-page"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                          />
                          <button
                            onClick={() => removeDropdownChild(index, childIndex)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addDropdownChild(index)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#D45614] hover:bg-[#D45614]/5 rounded-lg transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Add Sub-item
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Icon Toggles Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Eye className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Navbar Icons</h2>
              <p className="text-sm text-gray-500">Toggle which icons to show</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'showSearch', label: 'Search', icon: Search },
              { key: 'showWishlist', label: 'Wishlist', icon: Heart },
              { key: 'showCart', label: 'Cart', icon: ShoppingCart },
              { key: 'showAccount', label: 'Account', icon: User }
            ].map(({ key, label, icon: Icon }) => (
              <label key={key} className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all border-2 ${
                formData[key] ? 'border-[#D45614] bg-[#D45614]/5' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="checkbox"
                  checked={formData[key]}
                  onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))}
                  className="sr-only"
                />
                <Icon className={`w-5 h-5 ${formData[key] ? 'text-[#D45614]' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${formData[key] ? 'text-[#D45614]' : 'text-gray-600'}`}>{label}</span>
              </label>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.stickyNav}
                onChange={(e) => setFormData(prev => ({ ...prev, stickyNav: e.target.checked }))}
                className="w-4 h-4 text-[#D45614] border-gray-300 rounded focus:ring-[#D45614]"
              />
              <span className="text-sm font-medium text-gray-700">Sticky Navigation</span>
              <span className="text-xs text-gray-400">(Navbar stays visible when scrolling)</span>
            </label>
          </div>
        </section>

        {/* CTA Button Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <Plus className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Call-to-Action Button</h2>
                <p className="text-sm text-gray-500">Optional prominent button in navbar</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.cta.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, enabled: e.target.checked } }))}
                className="w-4 h-4 text-[#D45614] border-gray-300 rounded focus:ring-[#D45614]"
              />
              <span className="text-sm text-gray-600">Enable</span>
            </label>
          </div>

          {formData.cta.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Button Text</label>
                <input
                  type="text"
                  value={formData.cta.text}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, text: e.target.value } }))}
                  placeholder="Shop Now"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Button Link</label>
                <input
                  type="text"
                  value={formData.cta.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, link: e.target.value } }))}
                  placeholder="/shop"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default NavbarForm;
